import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { ChevronLeftIcon, ChevronRightIcon, EyeOffIcon, FolderIcon, MenuIcon, PlusIcon, SendIcon, SquareIcon, UsersIcon, XIcon } from "../icons";
import { Avatar } from "../avatar";
import type { AgentEvent, LockView, Message, ModelSpec, ProfileSummary, ProjectSummary, SessionSummary, Snapshot, User } from "../types";
import { useCommands } from "./CommandBar";
import { PlusMenu } from "./PlusMenu";
import { forkPosition } from "./branchPoints";
import { Messages, type OneShotCheck } from "./Messages";
import type { OneShotRunRef } from "./oneShotCard";
import { ModelPicker } from "./ModelPicker";
import { SessionSettings } from "./SessionSettings";
import { SessionTitle } from "./SessionTitle";
import { thinkingLevelOf } from "./modelChoice";
import { reduce } from "./chatReduce";
import { FIRST_BACKOFF_MS, nextBackoff, retryAfter } from "./streamRetry";
import { lockState } from "./sendLock";
import { parseWatching } from "../../../shared/watching/line.ts";
import { isVideoFile } from "./videoBinding";
import { closingFor } from "./videoClosing";
import { ChatSend } from "../components/chatSend";
import { VideoPanel, type VideoPanelHandle } from "./VideoPanel";
import { VideoSeek } from "./videoSeek";
import { useTaskMentions } from "./TaskMentions";
import { GET_TASK } from "../../../shared/wire/tasks.ts";
import { useIsNarrow } from "../viewport";
import { useChatScroll } from "./useChatScroll";

interface UploadedFile {
	filename: string;
	relPath: string;
}

/**
 * A soft keyboard's return key is a bare Enter and offers no Shift to pair it with, so on touch
 * Enter has to mean newline and the send button carries the send on its own.
 */
const sendOnEnter = !window.matchMedia?.("(pointer: coarse)").matches;

export function Chat({
	session,
	profile,
	onMenu,
	onBack,
	onRunEnded,
	onSent,
	onRenamed,
	readOnly = false,
	projectTitle,
	fileTo = [],
	onFile,
	me,
	users = [],
	sessions = [],
	onBranch,
	onOpenSession,
	onOpenLoop,
	initialDraft,
}: {
	session: SessionSummary;
	/** Every session in the list, for the fork markers and the parent's title. */
	sessions?: SessionSummary[];
	/** Cut a branch at an entry. Absent where nobody can branch. */
	onBranch?: (entryId: string) => void;
	onOpenSession?: (id: string) => void;
	/** Opens a loop or one-shot on the loops page. */
	onOpenLoop?: (id: string) => void;
	/** What the composer starts with: the message a branch was cut before. */
	initialDraft?: string;
	profile?: ProfileSummary;
	/** The person looking. Absent on a read-only transcript, where nobody sends. */
	me?: User;
	/** Every account, for naming whoever holds the lock or shares the session. */
	users?: User[];
	/** Named in the header when the session is filed under a project. */
	projectTitle?: string;
	/** The projects the owner can file this conversation under from the header menu. */
	fileTo?: ProjectSummary[];
	onFile?: (projectId: string) => void;
	onMenu: () => void;
	/** Shown as a back button; a loop iteration is opened from its loop and returns there. */
	onBack?: () => void;
	onRunEnded: () => void;
	/** The server accepted a message, before the agent answers it. */
	onSent?: () => void;
	/** The owner renamed the conversation. */
	onRenamed?: () => void;
	/** A loop iteration's transcript: watched, never typed into. */
	readOnly?: boolean;
}) {
	const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
	const [phase, setPhase] = useState<"connecting" | "starting" | "ready" | "failed">("connecting");
	const [startedAt, setStartedAt] = useState(() => Date.now());
	const [waited, setWaited] = useState(0);
	const [connected, setConnected] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	/** Child sessions of `delegate` calls still running, by call id. */
	const [delegateSessions, setDelegateSessions] = useState<Record<string, string>>({});
	/** Sessions of background jobs announced since the snapshot, by job id. */
	const [jobSessions, setJobSessions] = useState<Record<string, string>>({});
	/** Runs of `one_shot` calls still running, by call id. */
	const [oneShotRuns, setOneShotRuns] = useState<Record<string, OneShotRunRef>>({});
	/** The check each `one_shot` call's run is on, by call id. */
	const [oneShotChecks, setOneShotChecks] = useState<Record<string, OneShotCheck>>({});
	// A branch hands back the message it was cut before; the watching line on it is stale by now.
	// Failing that, whatever was left half-written in this session last time.
	const [draft, setDraft] = useState(
		() => parseWatching(initialDraft ?? "")?.rest ?? initialDraft ?? savedDraft(session.id),
	);
	const [pending, setPending] = useState<UploadedFile[]>([]);
	/* App gives Chat a key of the session id, so switching conversations unmounts this component
	   and everything typed into it. Uploads are already on the server; the text was not. */
	useEffect(() => {
		if (draft.trim()) localStorage.setItem(draftKey(session.id), draft);
		else localStorage.removeItem(draftKey(session.id));
	}, [draft, session.id]);
	const [uploading, setUploading] = useState(false);
	const [sending, setSending] = useState(false);
	// A video being watched beside the chat. It stays in the browser; the panel remembers the rest.
	const [videoFile, setVideoFile] = useState<File | null>(null);
	const [videoClosed, setVideoClosed] = useState(false);
	/* The conversation folded to a strip beside the video, giving the video the width. A message
	   arriving while folded unfolds it: the composer is folded away too, so it is a stop the video
	   made, a note, or someone else in a shared session -- all things to read. */
	const [folded, setFolded] = useState(false);
	const videoPanel = useRef<VideoPanelHandle>(null);
	useEffect(() => {
		setVideoFile(null);
		setVideoClosed(false);
	}, [session.id]);
	const [shareOpen, setShareOpen] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	// The session's own model choice; null follows the profile. Kept here so a switch shows at
	// once, before the parent reloads the session list.
	const [modelChoice, setModelChoice] = useState<ModelSpec | null>(session.model ?? null);
	useEffect(() => setModelChoice(session.model ?? null), [session.id]);
	// Ticks while someone else holds the lock, so its countdown and expiry show.
	const [now, setNow] = useState(() => Date.now());
	const { scroller, onScroll, followLatest } = useChatScroll(snapshot !== null);
	const fileInput = useRef<HTMLInputElement>(null);
	const mediaInput = useRef<HTMLInputElement>(null);
	const composerInput = useRef<HTMLTextAreaElement>(null);
	// Held in a ref so the SSE effect does not depend on a callback the parent recreates on
	// every render — depending on it tore the stream down and back up on each App re-render.
	const notifyRunEnded = useRef(onRunEnded);
	notifyRunEnded.current = onRunEnded;

	// Bumped to open a new stream after one ended for good; see streamRetry.ts.
	const [reconnect, setReconnect] = useState(0);
	const backoffMs = useRef(FIRST_BACKOFF_MS);

	// Another session starts from nothing. A reconnect keeps the transcript on screen.
	useEffect(() => {
		setSnapshot(null);
		setErrors([]);
		setPhase("connecting");
		setStartedAt(Date.now());
		setWaited(0);
	}, [session.id]);

	// One SSE stream per session: snapshot first, then live events. Every (re)connect resends the
	// snapshot, so the state heals itself as long as a stream is open.
	useEffect(() => {
		const es = new EventSource(`/api/sessions/${session.id}/events`);
		let retry: ReturnType<typeof setTimeout> | undefined;
		// Set where the server says this session is over, so the retry below leaves it closed.
		let closedForGood = false;

		es.addEventListener("starting", () => setPhase("starting"));

		es.addEventListener("snapshot", (e) => {
			setSnapshot(JSON.parse((e as MessageEvent).data) as Snapshot);
			setPhase("ready");
			setConnected(true);
			backoffMs.current = FIRST_BACKOFF_MS;
		});

		// The server sends `fatal` when the session cannot start at all. Closing here stops
		// EventSource from retrying the same failure forever behind a spinner.
		es.addEventListener("fatal", (e) => {
			const { message } = JSON.parse((e as MessageEvent).data) as { message: string };
			setErrors((v) => [...v, message]);
			setPhase("failed");
			closedForGood = true;
			es.close();
		});

		es.addEventListener("lock", (e) => {
			const lock = JSON.parse((e as MessageEvent).data) as LockView | null;
			setSnapshot((prev) => (prev ? { ...prev, lock } : prev));
		});

		// The owner stopped sharing the session with the person looking; the list refresh drops it.
		es.addEventListener("revoked", () => {
			setErrors((v) => [...v, "此对话已取消共享"]);
			setPhase("failed");
			closedForGood = true;
			es.close();
			notifyRunEnded.current();
		});

		es.addEventListener("agent", (e) => {
			const event = JSON.parse((e as MessageEvent).data) as AgentEvent;
			setSnapshot((prev) => (prev ? reduce(prev, event) : prev));
			if (event.type === "runtime_error") setErrors((v) => [...v, String(event.message)]);
			if (event.type === "delegate_started") setDelegateSessions((prev) => ({ ...prev, [String(event.callId)]: String(event.sessionId) }));
			if (event.type === "delegate_job_started") setJobSessions((prev) => ({ ...prev, [String(event.jobId)]: String(event.sessionId) }));
			if (event.type === "one_shot_started") setOneShotRuns((prev) => ({ ...prev, [String(event.callId)]: { loopId: String(event.loopId), iterationId: String(event.iterationId) } }));
			if (event.type === "one_shot_checking") {
				const check = { index: Number(event.index), of: Number(event.of), run: String(event.run), at: Date.now() };
				setOneShotChecks((prev) => ({ ...prev, [String(event.callId)]: check }));
			}
			if (event.type === "agent_end") {
				// agent_end only carries the messages added this run, not the full transcript, so ask the server for an authoritative snapshot
				void api
					.session(session.id)
					.then((r) => setSnapshot(r.snapshot))
					.catch(() => {});
				notifyRunEnded.current();
			}
		});
		es.onerror = () => {
			setConnected(false);
			const wait = retryAfter({ readyState: es.readyState, closedForGood, backoffMs: backoffMs.current });
			if (wait === null) return;
			retry = setTimeout(() => setReconnect((n) => n + 1), wait);
			backoffMs.current = nextBackoff(backoffMs.current);
		};
		es.onopen = () => setConnected(true);

		return () => {
			clearTimeout(retry);
			es.close();
		};
	}, [session.id, reconnect]);

	// Count up while waiting so a slow first start reads as "working", not "stuck".
	useEffect(() => {
		if (phase === "ready" || phase === "failed") return;
		const timer = setInterval(() => setWaited(Math.round((Date.now() - startedAt) / 1000)), 1000);
		return () => clearInterval(timer);
	}, [phase, startedAt]);

	const heldByOther = Boolean(snapshot?.lock && snapshot.lock.holder !== me?.id);
	useEffect(() => {
		if (!heldByOther) return;
		setNow(Date.now());
		const timer = setInterval(() => setNow(Date.now()), 30_000);
		return () => clearInterval(timer);
	}, [heldByOther, snapshot?.lock]);

	const messages: Message[] = snapshot?.messages ?? [];
	const streaming = snapshot?.isStreaming ?? false;
	useEffect(() => setFolded(false), [messages.length]);

	const nameOf = (userId: string) => users.find((u) => u.id === userId)?.name ?? userId;
	const sharedWith = session.sharedWith ?? [];
	const shared = sharedWith.length > 0;
	const isOwner = me?.id === session.owner;
	const canShare = isOwner && !readOnly && session.createdBy !== "loop" && profile?.sharing !== "owner-only";
	const lock = me
		? lockState({ lock: snapshot?.lock, me: me.id, owner: session.owner, shared, streaming, now, nameOf })
		: { blocked: false, note: null, handBack: false };
	// `@` picks a task only where the agent can read it: a conversation a person opened, not an anonymous one.
	const mentionable = Boolean(me) && !readOnly && Boolean(snapshot?.activeTools.includes(GET_TASK));
	const mentions = useTaskMentions({ enabled: mentionable && !lock.blocked, draft, setDraft, textarea: composerInput });
	const runSeconds = useRunClock(streaming);

	/** Returns null on success, or the message to show on failure -- the caller decides where it goes. */
	async function sendText(text: string): Promise<string | null> {
		if (!text.trim()) return null;
		followLatest();
		try {
			await api.send(session.id, text);
			onSent?.();
			return null;
		} catch (err) {
			return err instanceof ApiError ? err.message : "发送失败";
		}
	}

	/**
	 * Cleared only once the server has it.
	 *
	 * Clearing first and sending after lets a dropped connection eat a paragraph: the failure
	 * arrives as a banner and the text is already gone. The command dialog stays open on the
	 * same reasoning; this is the box people actually type in.
	 */
	async function send() {
		if (lock.blocked || sending) return;
		const typed = composeMessage(draft, pending);
		if (!typed.trim()) return;
		const text = typed.startsWith("/") ? typed : (videoPanel.current?.prefix() ?? "") + typed;
		setSending(true);
		const failure = await sendText(text);
		setSending(false);
		if (failure) {
			setErrors((v) => [...v, failure]);
			return;
		}
		setDraft("");
		setPending([]);
		localStorage.removeItem(draftKey(session.id));
	}

	const commands = useCommands({
		workflows: profile?.workflows ?? [],
		sessionId: session.id,
		streaming,
		onSend: sendText,
		onError: (message) => setErrors((v) => [...v, message]),
	});

	/** Puts text at the end of the draft and the caret after it. */
	function fill(text: string) {
		const next = draft && !/\s$/.test(draft) ? `${draft} ${text}` : `${draft}${text}`;
		setDraft(next);
		requestAnimationFrame(() => {
			const el = composerInput.current;
			if (!el) return;
			el.focus();
			el.setSelectionRange(next.length, next.length);
			mentions.onCaret(el);
		});
	}

	async function upload(files: FileList | null) {
		if (!files?.length) return;
		// A video is watched here, never uploaded: it opens in the panel and only its sound goes up.
		const videos = [...files].filter(isVideoFile);
		const others = [...files].filter((f) => !isVideoFile(f));
		if (videos.length > 0) openVideo(videos[0]!);
		if (videos.length > 1) setErrors((v) => [...v, "一次只能打开一个视频，已打开第一个"]);
		if (others.length === 0) {
			clearPickers();
			return;
		}
		setUploading(true);
		try {
			const res = await api.upload(session.id, others);
			setPending((v) => [...v, ...res.files]);
		} catch (err) {
			setErrors((v) => [...v, err instanceof ApiError ? err.message : "上传失败"]);
		} finally {
			setUploading(false);
			clearPickers();
		}
	}

	function clearPickers() {
		if (fileInput.current) fileInput.current.value = "";
		if (mediaInput.current) mediaInput.current.value = "";
	}

	function openVideo(file: File) {
		setVideoFile(file);
		setVideoClosed(false);
	}

	/** The panel's upload of a video's sound: the path comes back to it, not to the composer's chips. */
	async function uploadAudio(file: File): Promise<string> {
		const res = await api.upload(session.id, [file]);
		return res.files[0]!.relPath;
	}

	async function toggleShare(userId: string, on: boolean) {
		const next = on ? [...sharedWith, userId] : sharedWith.filter((u) => u !== userId);
		try {
			await api.setSharing(session.id, next);
			notifyRunEnded.current();
		} catch (err) {
			setErrors((v) => [...v, err instanceof ApiError ? err.message : "共享设置失败"]);
		}
	}

	async function switchModel(choice: ModelSpec | null) {
		try {
			const r = await api.setModel(session.id, choice);
			setModelChoice(choice);
			// Unpinning puts a live session back on its profile's model; the snapshot that follows says which.
			if (r.model) setSnapshot((prev) => (prev ? { ...prev, model: r.model, thinkingLevel: r.thinkingLevel ?? prev.thinkingLevel, pinned: true } : prev));
			else setSnapshot((prev) => (prev ? { ...prev, pinned: false } : prev));
			notifyRunEnded.current();
		} catch (err) {
			setErrors((v) => [...v, err instanceof ApiError ? err.message : "切换模型失败"]);
		}
	}

	async function handBack() {
		try {
			await api.releaseLock(session.id);
		} catch (err) {
			setErrors((v) => [...v, err instanceof ApiError ? err.message : "交还失败"]);
		}
	}

	// Under the title: the profile, then whatever applies.
	const notes: ReactNode[] = [session.profile];
	if (isOwner && shared) notes.push(`已共享给 ${sharedWith.map(nameOf).join("、")}`);
	if (session.anonymous)
		notes.push(
			<>
				<EyeOffIcon size={12} className="inline-glyph" /> 匿名
			</>,
		);
	if (projectTitle)
		notes.push(
			<>
				<FolderIcon size={12} className="inline-glyph" /> {projectTitle}
			</>,
		);
	if (me && !isOwner) notes.push(`来自 ${nameOf(session.owner)}`);
	if (session.parentId && session.createdBy === "delegate") {
		const parent = sessions.find((s) => s.id === session.parentId);
		notes.push(
			parent ? (
				<button className="link" onClick={() => onOpenSession?.(parent.id)}>
					由「{parent.title ?? "新对话"}」委派
				</button>
			) : (
				"委派"
			),
		);
	} else if (session.parentId) {
		// A branch names where it came from. A parent that is not in the list is archived or no
		// longer shared, so there is nothing to open.
		const parent = sessions.find((s) => s.id === session.parentId);
		const position = snapshot ? forkPosition(messages, session.forkEntryId) : null;
		const where = position === 0 ? "开头" : position ? `第 ${position} 条消息后` : "";
		notes.push(
			parent ? (
				<button className="link" onClick={() => onOpenSession?.(parent.id)}>
					从「{parent.title ?? "新对话"}」{where}分支
				</button>
			) : (
				"分支"
			),
		);
	}
	if (!connected) notes.push("连接中…");
	const canSwitchModel = Boolean(me) && !readOnly && !streaming && !lock.blocked && snapshot !== null;
	// A phone has no room beside the send button, so the header carries it there.
	const narrow = useIsNarrow();
	const modelPicker = profile && (
		<ModelPicker
			choice={modelChoice}
			running={{ provider: snapshot?.model?.provider ?? "", name: snapshot?.model?.id ?? "…", thinking: thinkingLevelOf(snapshot?.thinkingLevel) }}
			thinkingLevel={thinkingLevelOf(snapshot?.thinkingLevel ?? modelChoice?.thinking)}
			disabled={!canSwitchModel}
			onChange={(choice) => void switchModel(choice)}
		/>
	);

	return (
		<main className={videoFile && !videoClosed ? `chat with-video${folded ? " folded" : ""}` : "chat"}>
			<header className="chat-head">
				<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={onMenu}>
					<MenuIcon size={22} />
				</button>
				{onBack && (
					<button className="ghost icon-btn" aria-label="返回" title="返回" onClick={onBack}>
						<ChevronLeftIcon size={24} />
					</button>
				)}
				<Avatar name={session.profile} size={38} />
				<div className="titles">
					<SessionTitle
						session={session}
						canRename={isOwner && !readOnly && session.createdBy !== "loop"}
						onRenamed={() => onRenamed?.()}
						onError={(message) => setErrors((v) => [...v, message])}
					/>
					<span className="sub">
						{notes.map((note, i) => (
							<Fragment key={i}>
								{i > 0 && " · "}
								{note}
							</Fragment>
						))}
					</span>
				</div>
				{canShare && (
					<div className="share">
						<button className={`ghost share-toggle${shared ? " on" : ""}`} aria-label={shared ? "已共享" : "共享"} title={shared ? "已共享" : "共享"} onClick={() => setShareOpen((v) => !v)}>
							<UsersIcon size={20} />
							<span className="share-label">{shared ? "已共享" : "共享"}</span>
						</button>
						{shareOpen && (
							<div className="share-menu">
								{users.filter((u) => u.id !== session.owner).length === 0 ? (
									<p className="hint">暂无其他账号</p>
								) : (
									users
										.filter((u) => u.id !== session.owner)
										.map((u) => (
											<label key={u.id}>
												<input type="checkbox" checked={sharedWith.includes(u.id)} onChange={(e) => void toggleShare(u.id, e.target.checked)} />
												{u.name}
											</label>
										))
								)}
							</div>
						)}
					</div>
				)}
				{narrow && !readOnly && modelPicker}
				<SessionSettings sessionId={session.id} fileTo={onFile ? fileTo : []} onFile={onFile} onError={(message) => setErrors((v) => [...v, message])} />
			</header>

			<div className="chat-body">
				{!readOnly && !videoClosed && (
					<VideoPanel
						key={session.id}
						ref={videoPanel}
						sessionId={session.id}
						file={videoFile}
						opening={profile?.videoOpening ?? null}
						closing={closingFor(profile?.videoClosing ?? null, session.projectId)}
						upload={uploadAudio}
						send={sendText}
						busy={streaming || lock.blocked}
						onPick={openVideo}
						onClose={() => setVideoClosed(true)}
					/>
				)}
				<div className="chat-main wall">
					{videoFile && !videoClosed && (
						<button
							className="ghost chat-fold"
							title={folded ? "展开对话" : "收起对话"}
							aria-label={folded ? "展开对话" : "收起对话"}
							onClick={() => setFolded((v) => !v)}
						>
							{folded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
						</button>
					)}
					{errors.map((e, i) => (
						<p key={`e${i}`} className="banner error">
							{e}
							<button className="banner-close" title="关闭" onClick={() => setErrors((v) => v.filter((_, j) => j !== i))}>
								×
							</button>
						</p>
					))}

					<div className="messages" ref={scroller} onScroll={onScroll}>
						{snapshot === null ? (
							<Waiting phase={phase} seconds={waited} />
						) : (
							<VideoSeek value={videoFile && !videoClosed ? (seconds) => videoPanel.current?.seek(seconds) : null}>
								<ChatSend value={readOnly || streaming || lock.blocked ? null : sendText}>
								<Messages
									messages={messages}
									sessionId={session.id}
									components={snapshot?.profile.components ?? profile?.components ?? []}
									streaming={streaming}
									speakers={me && (shared || !isOwner) ? { first: nameOf(session.owner), me: me.name, nameOf, senders: snapshot?.senders } : undefined}
									branches={onBranch && !readOnly ? { sessions, onBranch, onOpen: (id) => onOpenSession?.(id) } : undefined}
									delegates={onOpenSession ? { started: delegateSessions, jobs: { ...snapshot?.jobSessions, ...jobSessions }, onOpen: onOpenSession } : undefined}
									oneShots={{ runs: oneShotRuns, checks: oneShotChecks, onOpen: onOpenLoop }}
								/>
								</ChatSend>
							</VideoSeek>
						)}

					</div>

					{readOnly ? (
						<p className="hint readonly-note">loop 对话，只读</p>
					) : (
					<div className="composer">
						{streaming && !heldByOther && (
							<div className="run-pill" role="status">
								<span className="run-dot" aria-hidden="true" />
								<span className="run-text">agent 工作中…</span>
								<span className="run-clock">{clock(runSeconds)}</span>
								<button className="run-stop" onClick={() => void api.abort(session.id)}>
									<SquareIcon size={12} />
									停止
								</button>
							</div>
						)}
						{lock.note && <p className="lock-note">{lock.note}</p>}
						{lock.handBack && !streaming && (
							<p className="lock-note">
								使用中
								<button className="ghost" onClick={() => void handBack()}>
									交还
								</button>
							</p>
						)}
						{pending.length > 0 && (
							<div className="chips">
								{pending.map((f) => (
									<span key={f.relPath} className="chip">
										{f.filename}
										<button aria-label={`移除 ${f.filename}`} onClick={() => setPending((v) => v.filter((x) => x.relPath !== f.relPath))}>
											<XIcon size={14} />
										</button>
									</span>
								))}
							</div>
						)}
						<div className="composer-row">
							{mentions.picker}
							{menuOpen && !lock.blocked && (
								<PlusMenu
									commands={profile?.commands ?? []}
									workflows={profile?.workflows ?? []}
									onMentionTask={mentionable ? () => fill("@") : undefined}
									onPickMedia={() => mediaInput.current?.click()}
									onPickFile={() => fileInput.current?.click()}
									onCommand={(cmd) => void commands.press(cmd)}
									opensDialog={commands.opensDialog}
									disabled={commands.disabled}
									onFill={fill}
									onClose={() => setMenuOpen(false)}
								/>
							)}
							<div className="composer-input">
								<button
									className={`plus-toggle${menuOpen ? " open" : ""}`}
									aria-label={menuOpen ? "收起" : "上传与 workflow"}
									title={menuOpen ? "收起" : "上传与 workflow"}
									aria-expanded={menuOpen}
									onClick={() => setMenuOpen((v) => !v)}
									disabled={uploading || lock.blocked}
								>
									{uploading ? "…" : menuOpen ? <XIcon size={20} /> : <PlusIcon size={22} />}
								</button>
								<input ref={fileInput} type="file" multiple hidden onChange={(e) => void upload(e.target.files)} />
								<input ref={mediaInput} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => void upload(e.target.files)} />
								<textarea
									ref={composerInput}
									value={draft}
									placeholder="输入消息"
									aria-label="消息"
									rows={1}
									disabled={lock.blocked}
									onChange={(e) => {
										// Typing is asking: the video waits.
										videoPanel.current?.pause();
										setDraft(e.target.value);
										mentions.onCaret(e.target);
									}}
									onSelect={(e) => mentions.onCaret(e.currentTarget)}
									onFocus={(e) => mentions.onCaret(e.currentTarget)}
									onBlur={mentions.onBlur}
									onKeyDown={(e) => {
										if (mentions.onKeyDown(e)) return;
										if (sendOnEnter && e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
											e.preventDefault();
											void send();
										}
									}}
								/>
							</div>
							{!narrow && modelPicker}
							<button
								className="send"
								aria-label={sending ? "发送中…" : "发送"}
								title="发送"
								onClick={() => void send()}
								disabled={lock.blocked || sending || (!draft.trim() && pending.length === 0)}
							>
								<SendIcon size={20} />
							</button>
						</div>
						{commands.dialog}
					</div>
					)}
				</div>
			</div>
		</main>
	);
}

/** Seconds since the run started, while it runs. */
function useRunClock(running: boolean): number {
	const [seconds, setSeconds] = useState(0);
	useEffect(() => {
		setSeconds(0);
		if (!running) return;
		const started = Date.now();
		const timer = setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
		return () => clearInterval(timer);
	}, [running]);
	return seconds;
}

const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

function Waiting({ phase, seconds }: { phase: "connecting" | "starting" | "ready" | "failed"; seconds: number }) {
	if (phase === "failed") return <p className="hint">对话启动失败</p>;
	const what = phase === "connecting" ? "正在连接服务端" : "正在启动 agent 运行时";
	return (
		<p className="hint streaming">
			{what}…{seconds > 0 && ` ${seconds}s`}
		</p>
	);
}

const draftKey = (id: string) => `hifi.draft.${id}`;

function savedDraft(id: string): string {
	try {
		return localStorage.getItem(draftKey(id)) ?? "";
	} catch {
		return "";
	}
}

/** Uploaded files ride along as paths; the agent reads them with the fs tools. */
function composeMessage(draft: string, files: UploadedFile[]): string {
	if (files.length === 0) return draft;
	const list = files.map((f) => `- ${f.relPath}`).join("\n");
	return `${draft.trim()}\n\nFile uploaded to (relative) path：\n${list}`.trim();
}
